import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Trash2, User, Calendar, ExternalLink, CheckCircle, XCircle, Clock, Edit, Save, X } from 'lucide-react';
import { Verification, BankInfo } from '../../types/verification';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

type Props = {
  verification: Verification;
};

export const VerificationDetails = ({ verification }: Props) => {
  const { translations } = useLanguage();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [bankInfo, setBankInfo] = useState<BankInfo>(verification.bankInfo || {
    bankName: '',
    accountNumber: '',
    swiftCode: '',
    bankBranch: '',
    ifscCode: '',
  });

  // Only show delete button if status is not approved
  const showDeleteButton = verification.status !== 'approved';
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  // Allow editing if user is the owner of this verification
  const canEditBankInfo = userRole === 'organizer' && verification.userId === userId;

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(firestore, 'verifications', verification.id));
      toast.success(translations?.verification?.deleteSuccess || 'Verification deleted successfully');
      navigate('/'); // Navigate back to home after deletion
    } catch (error: any) {
      console.error('Error deleting verification:', error);
      toast.error(translations?.verification?.deleteError || 'Failed to delete verification');
    }
  };

  const handleBankInfoUpdate = async () => {
    try {
      await updateDoc(doc(firestore, 'verifications', verification.id), {
        bankInfo,
        updatedAt: Date.now()
      });
      
      toast.success(translations?.verification?.bankInfoUpdated || 'Bank information updated successfully');
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error updating bank info:', error);
      toast.error(translations?.verification?.bankInfoError || 'Failed to update bank information');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-500 bg-green-50';
      case 'rejected':
        return 'text-red-500 bg-red-50';
      default:
        return 'text-yellow-500 bg-yellow-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return CheckCircle;
      case 'rejected':
        return XCircle;
      default:
        return Clock;
    }
  };

  const StatusIcon = getStatusIcon(verification.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {translations?.verification?.detailsTitle || 'Verification Details'}
            </h2>
            <div className="flex flex-col gap-2">
              <p className="text-gray-500">
                {translations?.verification?.created || 'Created'}: {formatDate(verification.createdAt)}
              </p>
              <p className="text-gray-500">
                {translations?.verification?.lastUpdated || 'Last updated'}: {formatDate(verification.updatedAt)}
              </p>
              {/* Status Badge */}
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(verification.status)}`}>
                <StatusIcon size={16} />
                <span className="capitalize">{verification.status}</span>
              </div>
             
              {/* Show rejection reason if rejected */}
              {verification.status === 'rejected' && verification.rejectionReason && (
                <div className="mt-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <p className="font-medium">
                    {translations?.verification?.rejectionReason || 'Rejection Reason'}:
                  </p>
                  <p>{verification.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {/* Delete Button - Only show if not approved */}
            {showDeleteButton && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <Trash2 size={20} />
                <span>{translations?.verification?.delete || 'Delete'}</span>
              </motion.button>
            )}

            {/* Contract View/Download */}
             {false && verification.contractUrl && (
              <motion.a
                href={verification.contractUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <ExternalLink size={20} />
                <span>{translations?.verification?.viewContract || 'View Contract'}</span>
              </motion.a>
            )}
            
            {/* Signed Contract View/Download */}
            {verification.signedContractUrl && (
              <motion.a
                href={verification.signedContractUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
              >
                <ExternalLink size={20} />
                <span>{translations?.verification?.viewContract || 'View Signed Contract'}</span>
              </motion.a>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary-50 p-4 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-2">
            <User className="text-primary-500" size={20} />
            <span className="font-medium">
              {translations?.verification?.userType || 'User Type'}
            </span>
          </div>
          <p className="text-lg font-semibold capitalize">{verification.userType}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary-50 p-4 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="text-primary-500" size={20} />
            <span className="font-medium">
              {translations?.verification?.createdAt || 'Created At'}
            </span>
          </div>
          <p className="text-lg font-semibold">{formatDate(verification.createdAt)}</p>
        </motion.div>
      </div>

      {/* User Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border p-6"
      >
        <h4 className="font-medium mb-4 flex items-center gap-2">
          <User size={20} className="text-primary-500" />
          {translations?.verification?.personalInfo || 'Personal Information'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(verification.userInfo).map(([key, value]) => (
            <div key={key}>
              <label className="block text-sm text-gray-500 capitalize">
                {key.replace(/_/g, ' ')}
              </label>
              <p className="mt-1 font-medium">{value}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Bank Info (if organizer) */}
      {verification.userType === 'organizer' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium flex items-center gap-2">
              {translations?.verification?.bankInfo || 'Bank Information'}
            </h4>
            {canEditBankInfo && !isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700"
              >
                <Edit size={16} />
                <span>{translations?.common?.edit || 'Edit'}</span>
              </button>
            )}
          </div>
          
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {translations?.verification?.bankName || 'Bank Name'}
                  </label>
                  <input 
                    type="text"
                    value={bankInfo.bankName || ''}
                    onChange={(e) => setBankInfo({...bankInfo, bankName: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {translations?.verification?.accountNumber || 'Account Number'}
                  </label>
                  <input 
                    type="text"
                    value={bankInfo.accountNumber || ''}
                    onChange={(e) => setBankInfo({...bankInfo, accountNumber: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {translations?.verification?.swiftCode || 'SWIFT Code'}
                  </label>
                  <input 
                    type="text"
                    value={bankInfo.swiftCode || ''}
                    onChange={(e) => setBankInfo({...bankInfo, swiftCode: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {translations?.verification?.bankBranch || 'Bank Branch'}
                  </label>
                  <input 
                    type="text"
                    value={bankInfo.bankBranch || ''}
                    onChange={(e) => setBankInfo({...bankInfo, bankBranch: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {translations?.verification?.ifscCode || 'IFSC Code'}
                  </label>
                  <input 
                    type="text"
                    value={bankInfo.ifscCode || ''}
                    onChange={(e) => setBankInfo({...bankInfo, ifscCode: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setBankInfo(verification.bankInfo || {
                      bankName: '',
                      accountNumber: '',
                      swiftCode: '',
                      bankBranch: '',
                      ifscCode: '',
                    });
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X size={16} />
                  <span>{translations?.common?.cancel || 'Cancel'}</span>
                </button>
                
                <button
                  onClick={handleBankInfoUpdate}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  <Save size={16} />
                  <span>{translations?.common?.save || 'Save'}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {verification.bankInfo ? (
                Object.entries(verification.bankInfo).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-sm text-gray-500 capitalize">
                      {key.replace(/_/g, ' ')}
                    </label>
                    <p className="mt-1 font-medium">{value || '-'}</p>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center text-gray-500 py-4">
                  {translations?.verification?.noBankInfo || 'No bank information provided'}
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Digital Signature */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border p-6"
      >
        <h4 className="font-medium mb-4">
          {translations?.verification?.digitalSignature || 'Digital Signature'}
        </h4>
        <p className="font-medium">{verification.signature}</p>
      </motion.div>
    </div>
  );
};
